
/**
 * Route generation utilities for consistent navigation across the application
 * Centralizes route logic and ensures type-safe navigation
 */

export type CourseType = 
  | 'react' 
  | 'sql' 
  | 'python' 
  | 'ai' 
  | 'java' 
  | 'aws' 
  | 'azure' 
  | 'gcp' 
  | 'dotnet' 
  | 'dataengineering' 
  | 'javascript' 
  | 'ml' 
  | 'datascience' 
  | 'powerbi' 
  | 'tableau' 
  | 'devops'
  | 'appsec'
  | 'infrastructure'
  | 'frontend-advanced'
  | 'testing-modern'
  | 'web3-development'
  | 'serverless-edge'
  | 'rpa-tools'
  | 'etl'
  | 'workflow-automation'
  | 'basic';

/**
 * Determines the course type based on course ID patterns
 * @param courseId - The course identifier
 * @returns The determined course type
 */
export function determineCourseType(courseId: string): CourseType {
  // Direct course type mapping based on ID patterns
  if (courseId.includes('sql') || courseId.startsWith('sql-')) return 'sql';
  if (courseId.includes('python') || courseId.startsWith('python-')) return 'python';
  if (courseId.includes('ai') || courseId.startsWith('ai-')) return 'ai';
  if (courseId.includes('java') || courseId.startsWith('java-')) return 'java';
  if (courseId.includes('aws') || courseId.startsWith('aws-')) return 'aws';
  if (courseId.includes('azure') || courseId.startsWith('azure-')) return 'azure';
  if (courseId.includes('gcp') || courseId.startsWith('gcp-')) return 'gcp';
  if (courseId.includes('dotnet') || courseId.startsWith('dotnet-')) return 'dotnet';
  if (courseId.includes('dataengineering') || courseId.startsWith('dataeng-')) return 'dataengineering';
  if (courseId.includes('javascript') || courseId.startsWith('js-')) return 'javascript';
  if (courseId.includes('ml') || courseId.startsWith('ml-')) return 'ml';
  if (courseId.includes('datascience') || courseId.startsWith('datascience-')) return 'datascience';
  if (courseId.includes('powerbi') || courseId.startsWith('powerbi-')) return 'powerbi';
  if (courseId.includes('tableau') || courseId.startsWith('tableau-')) return 'tableau';
  if (courseId.includes('devops') || courseId.startsWith('devops-')) return 'devops';
  if (courseId.includes('appsec') || courseId.startsWith('appsec-') || courseId.includes('owasp')) return 'appsec';
  if (courseId.includes('infrastructure') || courseId.startsWith('infrastructure-')) return 'infrastructure';
  if (courseId.includes('frontend-advanced') || courseId.startsWith('frontend-advanced-')) return 'frontend-advanced';
  if (courseId.includes('testing-modern') || courseId.startsWith('testing-modern-')) return 'testing-modern';
  if (courseId.includes('web3-development') || courseId.startsWith('web3-development-')) return 'web3-development';
  // Selenium courses detection
  if (courseId.includes('selenium') || courseId.startsWith('selenium-')) return 'testing-modern';
  // C# courses detection
  if (courseId.includes('csharp') || courseId.startsWith('csharp-')) return 'dotnet';
  // RPA courses detection
  if (courseId.includes('uipath') || courseId.includes('power-automate') || 
      courseId.includes('automation-anywhere') || courseId.includes('blue-prism') || 
      courseId.includes('rpa-') || courseId.startsWith('rpa-')) return 'rpa-tools';
  // ETL courses detection
  if (courseId.includes('etl') || courseId.includes('dbt') || courseId.includes('ssis') || 
      courseId.includes('informatica') || courseId.includes('glue') || courseId.includes('dataflow') ||
      courseId.startsWith('etl-') || courseId.endsWith('-etl')) return 'etl';
  // Enhanced serverless-edge detection for specific course IDs
  if (courseId.includes('serverless') || courseId.includes('edge') || 
      courseId === 'serverless-architecture' || courseId === 'edge-computing' ||
      courseId.startsWith('serverless-') || courseId.startsWith('edge-')) return 'serverless-edge';
  // Workflow automation courses detection (n8n, zapier, make.com, etc.)
  if (courseId.includes('n8n') || courseId.includes('zapier') || courseId.includes('make-com') ||
      courseId.includes('workflow-automation') || courseId.startsWith('workflow-')) return 'workflow-automation';
  if (courseId.includes('react') || courseId.startsWith('react-') || courseId === 'basic-react') return 'react';
  
  return 'basic';
}

/**
 * Gets the route prefix for a given course type
 * @param courseType - The course type
 * @param courseId - Optional course ID for additional context
 * @returns The route prefix path
 */
export function getRoutePrefix(courseType: CourseType, courseId?: string): string {
  switch (courseType) {
    case 'sql': return '/sql-courses';
    case 'python': return '/python-courses';
    case 'ai': return '/ai-courses';
    case 'java': return '/java-courses';
    case 'aws': return '/aws-courses';
    case 'azure': return '/azure-courses';
    case 'gcp': return '/gcp-courses';
    case 'dotnet': return '/dotnet-courses';
    case 'dataengineering': return '/dataengineering-courses';
    case 'javascript': return '/javascript-courses';
    case 'ml': return '/ml-courses';
    case 'datascience': return '/datascience-courses';
    case 'powerbi': return '/powerbi-courses';
    case 'tableau': return '/tableau-courses';
    case 'infrastructure': return '/infrastructure-courses';
    case 'frontend-advanced': return '/frontend-advanced-courses';
    case 'testing-modern': return '/testing-modern-courses';
    case 'web3-development': return '/web3-development-courses';
    case 'serverless-edge': return '/serverless-edge-courses';
    case 'rpa-tools': return '/rpa-tools-courses';
    case 'etl': return '/etl-courses';
    case 'workflow-automation': return '/workflow-automation-courses';
    case 'react':
    case 'basic':
    default:
      return '/courses';
  }
}

/**
 * Generates the correct route for a course based on its type
 * @param courseId - The course identifier
 * @param courseType - Optional explicit course type
 * @returns The course route path
 */
export function generateCourseRoute(courseId: string, courseType?: CourseType): string {
  const finalType = courseType || determineCourseType(courseId);
  
  switch (finalType) {
    case 'sql': return `/sql-courses/${courseId}`;
    case 'python': return `/python-courses/${courseId}`;
    case 'ai': return `/ai-courses/${courseId}`;
    case 'java': return `/java-courses/${courseId}`;
    case 'aws': return `/aws-courses/${courseId}`;
    case 'azure': return `/azure-courses/${courseId}`;
    case 'gcp': return `/gcp-courses/${courseId}`;
    case 'dotnet': return `/dotnet-courses/${courseId}`;
    case 'dataengineering': return `/dataengineering-courses/${courseId}`;
    case 'javascript': return `/javascript-courses/${courseId}`;
    case 'ml': return `/ml-courses/${courseId}`;
    case 'datascience': return `/datascience-courses/${courseId}`;
    case 'powerbi': return `/powerbi-courses/${courseId}`;
    case 'tableau': return `/tableau-courses/${courseId}`;
    case 'infrastructure': return `/infrastructure-courses/${courseId}`;
    case 'frontend-advanced': return `/frontend-advanced-courses/${courseId}`;
    case 'testing-modern': return `/testing-modern-courses/${courseId}`;
    case 'web3-development': return `/web3-development-courses/${courseId}`;
    case 'serverless-edge': return `/serverless-edge-courses/${courseId}`;
    case 'rpa-tools': return `/rpa-tools-courses/${courseId}`;
    case 'etl': return `/etl-courses/${courseId}`;
    case 'workflow-automation': return `/workflow-automation-courses/${courseId}`;
    case 'react':
    case 'basic':
    default:
      return `/courses/${courseId}`;
  }
}

/**
 * Generates the correct route for a module based on course type
 * @param courseId - The course identifier
 * @param moduleId - The module identifier
 * @param courseType - Optional explicit course type
 * @returns The module route path
 */
export function generateModuleRoute(courseId: string, moduleId: string, courseType?: CourseType): string {
  const finalType = courseType || determineCourseType(courseId);
  
  switch (finalType) {
    case 'sql': return `/sql-courses/${courseId}/${moduleId}`;
    case 'python': return `/python-courses/${courseId}/${moduleId}`;
    case 'ai': return `/ai-courses/${courseId}/${moduleId}`;
    case 'java': return `/java-courses/${courseId}/${moduleId}`;
    case 'aws': return `/aws-courses/${courseId}/${moduleId}`;
    case 'azure': return `/azure-courses/${courseId}/${moduleId}`;
    case 'gcp': return `/gcp-courses/${courseId}/${moduleId}`;
    case 'dotnet': return `/dotnet-courses/${courseId}/${moduleId}`;
    case 'dataengineering': return `/dataengineering-courses/${courseId}/${moduleId}`;
    case 'javascript': return `/javascript-courses/${courseId}/${moduleId}`;
    case 'ml': return `/ml-courses/${courseId}/${moduleId}`;
    case 'datascience': return `/datascience-courses/${courseId}/${moduleId}`;
    case 'powerbi': return `/powerbi-courses/${courseId}/${moduleId}`;
    case 'tableau': return `/tableau-courses/${courseId}/${moduleId}`;
    case 'infrastructure': return `/infrastructure-courses/${courseId}/${moduleId}`;
    case 'frontend-advanced': return `/frontend-advanced-courses/${courseId}/${moduleId}`;
    case 'testing-modern': return `/testing-modern-courses/${courseId}/${moduleId}`;
    case 'web3-development': return `/web3-development-courses/${courseId}/${moduleId}`;
    case 'serverless-edge': return `/serverless-edge-courses/${courseId}/${moduleId}`;
    case 'rpa-tools': return `/rpa-tools-courses/${courseId}/${moduleId}`;
    case 'etl': return `/etl-courses/${courseId}/${moduleId}`;
    case 'workflow-automation': return `/workflow-automation-courses/${courseId}/${moduleId}`;
    case 'react':
    case 'basic':
    default:
      return `/courses/${courseId}/${moduleId}`;
  }
}

/**
 * Gets the course listing route for a given course type
 * @param courseType - The course type
 * @returns The course listing route
 */
export function getCourseListingRoute(courseType: CourseType): string {
  switch (courseType) {
    case 'sql': return '/sql-courses';
    case 'python': return '/python-courses';
    case 'ai': return '/ai-courses';
    case 'java': return '/java-courses';
    case 'aws': return '/aws-courses';
    case 'azure': return '/azure-courses';
    case 'gcp': return '/gcp-courses';
    case 'dotnet': return '/dotnet-courses';
    case 'dataengineering': return '/dataengineering-courses';
    case 'javascript': return '/javascript-courses';
    case 'ml': return '/ml-courses';
    case 'datascience': return '/datascience-courses';
    case 'powerbi': return '/powerbi-courses';
    case 'tableau': return '/tableau-courses';
    case 'infrastructure': return '/infrastructure-courses';
    case 'frontend-advanced': return '/frontend-advanced-courses';
    case 'testing-modern': return '/testing-modern-courses';
    case 'web3-development': return '/web3-development-courses';
    case 'serverless-edge': return '/serverless-edge-courses';
    case 'rpa-tools': return '/rpa-tools-courses';
    case 'etl': return '/etl-courses';
    case 'workflow-automation': return '/workflow-automation-courses';
    case 'react': return '/react-courses';
    case 'basic':
    default:
      return '/courses';
  }
}
